import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { confirmDelete } from "@/lib/confirm";
import { Loader2, Plus, X, UserCog } from "lucide-react";

export const FlowRolesManager = ({ canEdit }) => {
  const [roles, setRoles] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/agency/flow-roles").then(({ data }) => setRoles(data.roles || [])).catch(() => setRoles([])); }, []);

  const save = async (next) => {
    setSaving(true);
    try { const { data } = await api.put("/agency/flow-roles", { roles: next }); setRoles(data.roles); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const add = () => {
    const name = draft.trim();
    if (!name || !roles) return;
    if (roles.some((r) => r.name.toLowerCase() === name.toLowerCase())) { toast.error("That role already exists"); return; }
    setDraft(""); save([...roles, { name }]);
  };

  return (
    <div className="rounded-3xl border border-[#EEEFEF] bg-white p-5 mb-6" data-testid="flow-roles-manager">
      <div className="flex items-center gap-2 mb-1">
        <span className="h-8 w-8 rounded-xl bg-[#EDE7FF] text-[#723FFF] flex items-center justify-center"><UserCog className="h-4 w-4" /></span>
        <h3 className="font-display text-base font-semibold text-[#06060C]">Flow Roles</h3>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#c4c5cc]" />}
      </div>
      <p className="text-xs text-[#9FA1A4] mb-3">Assign flow tasks to a role. Set who plays each role in each client’s settings → Flow Roles; imports and new projects use it.</p>
      {roles === null ? <Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc]" /> : (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="flow-roles-list">
          {roles.length === 0 && <span className="text-xs text-[#c4c5cc]">No roles yet.</span>}
          {roles.map((r) => (
            <span key={r.id} data-testid={`flow-role-chip-${r.id}`} className="inline-flex items-center gap-1 rounded-full border border-[#EEEFEF] bg-[#F6F6FD] pl-3 pr-1.5 py-1 text-xs text-[#06060C]">
              {r.name}
              {canEdit && <button data-testid={`flow-role-remove-${r.id}`} onClick={async () => { if (await confirmDelete({ message: `Remove the “${r.name}” role? Flow tasks using it will import to the Scheduler until you pick a new role.`, confirmText: "Remove" })) save(roles.filter((x) => x.id !== r.id)); }} disabled={saving} className="text-[#c4c5cc] hover:text-rose-600"><X className="h-3 w-3" /></button>}
            </span>
          ))}
        </div>
      )}
      {canEdit && <div className="flex items-center gap-2 max-w-sm">
        <Input data-testid="flow-role-input" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} maxLength={60} placeholder="e.g. Copywriter" className="bg-[#F6F6FD] border-[#EEEFEF] h-9" />
        <Button data-testid="flow-role-add" size="sm" onClick={add} disabled={saving || roles === null || !draft.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>}
    </div>
  );
};
